import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { auth } from '../../firebase/firebase-config';
import { formatRoleLabel } from '../../platform/roleLabels';
import {
  displayUserName,
  userCellSecondaryLine,
  userInitialsSource,
} from '../../utils/userDisplay';
import { USERS_PAGE_SIZE } from './userManagementConstants';
import UserManagementAvatar from './UserManagementAvatar';
import UserActionsMenu from './UserActionsMenu';

export default function UserManagementTable({
  searchQuery,
  onSearchChange,
  filteredUsers,
  paginatedUsers,
  users,
  units,
  safePageIndex,
  totalPages,
  rangeStart,
  rangeEnd,
  showPagination,
  onPageChange,
  onEdit,
  onPassword,
  onDelete,
}) {
  return (
    <div className="user-management-list-wrapper">
      <section className="user-management-section">
        <h2 className="platform-section-title">Iskanje</h2>
        <div className="user-management-search">
          <Form.Control
            type="text"
            placeholder="Išči po imenu, emailu, delovnem mestu, oddelku…"
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            aria-label="Išči po imenu, emailu, telefonu, delovnem mestu, oddelku, enoti ali vlogi"
            autoComplete="off"
          />
        </div>
      </section>

      <section className="user-management-section">
        <h2 className="platform-section-title">
          Seznam uporabnikov
          {filteredUsers.length > 0 && (
            <span className="user-management-section__count">{filteredUsers.length}</span>
          )}
        </h2>
        {filteredUsers.length === 0 ? (
          <div className="user-management-empty">
            {searchQuery.trim()
              ? 'Noben uporabnik ne ustreza iskanju.'
              : 'Ni uporabnikov v tej organizaciji.'}
          </div>
        ) : (
          <>
            <div className="user-management-table-panel">
              <div className="user-management-table-scroll">
                <div className="user-management-table-grid" role="table" aria-label="Seznam uporabnikov">
                  <div className="user-management-table-grid__row user-management-table-grid__row--head" role="row">
                    <div role="columnheader">Uporabnik</div>
                    <div role="columnheader">Telefon</div>
                    <div role="columnheader">Delovno mesto</div>
                    <div role="columnheader">Oddelek</div>
                    <div role="columnheader">Enota</div>
                    <div role="columnheader">Vloga</div>
                    <div className="user-management-table-grid__actions-head" role="columnheader">Akcije</div>
                  </div>

                  {paginatedUsers.map((userObj) => {
                    const unitName = units.find((unit) => unit.id === userObj.unit)?.unitName || '—';
                    const userName = displayUserName(userObj);
                    const userSubtitle = userCellSecondaryLine(userObj);
                    const isSelf = userObj.id === auth.currentUser?.uid;

                    return (
                      <div
                        key={userObj.id}
                        className="user-management-table-grid__row user-management-table-grid__row--body"
                        role="row"
                      >
                        <div className="user-management-table-grid__cell user-management-table-grid__cell--user" role="cell">
                          <div className="user-management-user-cell">
                            <UserManagementAvatar
                              user={userObj}
                              displayName={userInitialsSource(userObj)}
                            />
                            <div className="user-management-user-cell__body">
                              <div className="user-management-user-cell__name">{userName}</div>
                              {userSubtitle && (
                                <div className="user-management-user-cell__email">{userSubtitle}</div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="user-management-table-grid__cell user-management-table__phone" role="cell">
                          {userObj.phone || '—'}
                        </div>
                        <div
                          className="user-management-table-grid__cell user-management-table__work"
                          role="cell"
                          title={userObj.jobPosition || ''}
                        >
                          {userObj.jobPosition || '—'}
                        </div>
                        <div
                          className="user-management-table-grid__cell user-management-table__work"
                          role="cell"
                          title={userObj.workArea || ''}
                        >
                          {userObj.workArea || '—'}
                        </div>
                        <div
                          className="user-management-table-grid__cell user-management-table__unit"
                          role="cell"
                          title={unitName}
                        >
                          {unitName}
                        </div>
                        <div className="user-management-table-grid__cell" role="cell">
                          <span className={`user-management-role user-management-role--${userObj.role || 'user'}`}>
                            {formatRoleLabel(userObj.role)}
                          </span>
                        </div>
                        <div className="user-management-table-grid__cell user-management-table-grid__cell--actions" role="cell">
                          <UserActionsMenu
                            user={userObj}
                            onEdit={onEdit}
                            onPassword={onPassword}
                            onDelete={onDelete}
                            isSelf={isSelf}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {showPagination && (
              <nav className="user-management-pagination" aria-label="Strani uporabnikov">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="user-management-pagination__btn"
                  onClick={() => onPageChange(Math.max(0, safePageIndex - 1))}
                  disabled={safePageIndex === 0}
                >
                  <FaChevronLeft aria-hidden="true" />
                  <span>Prejšnja</span>
                </Button>

                <div className="user-management-pagination__info">
                  <span className="user-management-pagination__page">
                    Stran {safePageIndex + 1} / {totalPages}
                  </span>
                  <span className="user-management-pagination__range">
                    Prikazano {rangeStart}–{rangeEnd}
                    {searchQuery.trim() ? ` od ${filteredUsers.length}` : ''}
                    {' · '}
                    Skupaj {users.length} {users.length === 1 ? 'uporabnik' : 'uporabnikov'}
                  </span>
                </div>

                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="user-management-pagination__btn"
                  onClick={() => onPageChange(Math.min(totalPages - 1, safePageIndex + 1))}
                  disabled={safePageIndex >= totalPages - 1}
                >
                  <span>Naslednja</span>
                  <FaChevronRight aria-hidden="true" />
                </Button>
              </nav>
            )}
          </>
        )}
      </section>
    </div>
  );
}

export { USERS_PAGE_SIZE };
